import React, { useEffect, useRef, useState, useCallback } from "react";
// import "./Home.scss";

const AUTO_INTERVAL = 3000;
const THUMB_VISIBLE = 6; // how many thumbs to show at once

export default function FeaturedWorkSection({ registerIntent, url }) {

  const PROJECTS = [
    { id: 1,  number: "01", image: url + "images/new-home/featured-work/1.jpg",  thumb: url + "images/new-home/featured-work/1.jpg"  },
    { id: 2,  number: "02", image: url + "images/new-home/featured-work/2.jpg",  thumb: url + "images/new-home/featured-work/2.jpg"  },
    { id: 3,  number: "03", image: url + "images/new-home/featured-work/3.jpg",  thumb: url + "images/new-home/featured-work/3.jpg"  },
    { id: 4,  number: "04", image: url + "images/new-home/featured-work/4.jpg",  thumb: url + "images/new-home/featured-work/4.jpg"  },
    { id: 5,  number: "05", image: url + "images/new-home/featured-work/5.jpg",  thumb: url + "images/new-home/featured-work/5.jpg"  },
    { id: 6,  number: "06", image: url + "images/new-home/featured-work/6.jpg",  thumb: url + "images/new-home/featured-work/6.jpg"  },
    { id: 7,  number: "07", image: url + "images/new-home/featured-work/7.jpg",  thumb: url + "images/new-home/featured-work/7.jpg"  },
    { id: 8,  number: "08", image: url + "images/new-home/featured-work/8.jpg",  thumb: url + "images/new-home/featured-work/8.jpg"  },
    { id: 9,  number: "09", image: url + "images/new-home/featured-work/9.jpg",  thumb: url + "images/new-home/featured-work/9.jpg"  },
    { id: 10, number: "10", image: url + "images/new-home/featured-work/10.jpg", thumb: url + "images/new-home/featured-work/10.jpg" },
    { id: 11, number: "11", image: url + "images/new-home/featured-work/11.jpg", thumb: url + "images/new-home/featured-work/11.jpg" },
  ];

  const [active,   setActive]   = useState(0);
  const [prev,     setPrev]     = useState(null);
  const [dir,      setDir]      = useState(1);
  const [paused,   setPaused]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [loadedImages, setLoadedImages] = useState(() => new Set([0]));

  const activeRef    = useRef(0);
  const progressRef  = useRef(null);
  const startTimeRef = useRef(Date.now());

  // Preload the remaining project images one at a time, in the
  // background, so they don't all compete for connections on mount.
  useEffect(() => {
    let cancelled = false;
    const queue = PROJECTS.map((_, i) => i).filter((i) => i !== 0);

    const preloadNext = ([idx, ...rest]) => {
      if (cancelled || idx === undefined) return;
      const img = new Image();
      img.onload = img.onerror = () => {
        if (cancelled) return;
        setLoadedImages((prev) => new Set(prev).add(idx));
        preloadNext(rest);
      };
      img.src = PROJECTS[idx].image;
    };

    preloadNext(queue);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the user navigates to a project before its image has finished
  // preloading, fetch it immediately instead of waiting in queue.
  useEffect(() => {
    if (loadedImages.has(active)) return;
    const img = new Image();
    img.onload = img.onerror = () => {
      setLoadedImages((prev) => new Set(prev).add(active));
    };
    img.src = PROJECTS[active].image;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const goTo = useCallback((idx, direction = 1) => {
    if (idx === activeRef.current) return;
    setPrev(activeRef.current);
    setDir(direction);
    activeRef.current = idx;
    setActive(idx);
    setProgress(0);
    startTimeRef.current = Date.now();
  }, []);

  const advance = useCallback(() => {
    const next = (activeRef.current + 1) % PROJECTS.length;
    goTo(next, 1);
  }, [goTo, PROJECTS.length]);

  useEffect(() => {
    if (paused) return;
    const tick = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const pct = Math.min((elapsed / AUTO_INTERVAL) * 100, 100);
      setProgress(pct);
      if (elapsed >= AUTO_INTERVAL) {
        advance();
        startTimeRef.current = Date.now();
      }
      progressRef.current = requestAnimationFrame(tick);
    };
    progressRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(progressRef.current);
  }, [paused, advance]);

  useEffect(() => {
    if (!registerIntent) return;
    registerIntent((scrollDir) => {
      const next = activeRef.current + scrollDir;
      if (next >= 0 && next < PROJECTS.length) {
        goTo(next, scrollDir);
        startTimeRef.current = Date.now();
        return true;
      }
      return false;
    });
  }, [registerIntent, goTo, PROJECTS.length]);

  // ── Compute the sliding window of 6 thumbs centered on active ──────────
  // Window slides so active is always visible, clamped to array bounds.
  const windowStart = Math.min(
    Math.max(0, active - Math.floor(THUMB_VISIBLE / 2)),
    Math.max(0, PROJECTS.length - THUMB_VISIBLE)
  );
  const visibleThumbs = PROJECTS.slice(windowStart, windowStart + THUMB_VISIBLE);

  const p = PROJECTS[active];

  return (
    <section
      id="work-section"
      className="fw"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => { setPaused(false); startTimeRef.current = Date.now(); }}
    >
      {/* Background images */}
      {PROJECTS.map((proj, i) => (
        <div
          key={proj.id}
          className={`fw__bg ${i === active ? "fw__bg--active" : ""}`}
          style={loadedImages.has(i) ? { backgroundImage: `url(${proj.image})` } : undefined}
        />
      ))}
      <div className="fw__bg-overlay" />

      {/* Main layout */}
      <div className="fw__body">

        {/* CENTER: screen mockup */}
        <div className="fw__screen-wrap">
          <div className="fw__screen">
            <div className="fw__screen-bezel">
              <div className="fw__screen-cam" />
            </div>
            <div className="fw__screen-display">
              {PROJECTS.map((proj, i) => (
                <div
                  key={proj.id}
                  className={`fw__screen-img ${
                    i === active ? "fw__screen-img--active" :
                    i === prev   ? `fw__screen-img--exit fw__screen-img--exit-${dir > 0 ? "left" : "right"}` : ""
                  }`}
                  style={loadedImages.has(i) ? { backgroundImage: `url(${proj.image})` } : undefined}
                />
              ))}
              <div className="fw__screen-glare" />
            </div>
            <div className="fw__screen-progress">
              <div
                className="fw__screen-progress-fill"
                style={{ width: `${progress}%`, background: p.accent }}
              />
            </div>
          </div>
          <div className="fw__stand">
            <div className="fw__stand-neck" />
            <div className="fw__stand-base" />
          </div>
        </div>

        {/* RIGHT: thumbnail sidebar — shows 6 at a time, window slides */}
        <div className="fw__sidebar">
          <p className="fw__sidebar-label">Projects</p>
          <div className="fw__thumbs">
            {visibleThumbs.map((proj) => {
              // original index in PROJECTS array
              const origIdx = PROJECTS.findIndex(pr => pr.id === proj.id);
              const isActive = origIdx === active;
              return (
                <button
                  key={proj.id}
                  className={`fw__thumb ${isActive ? "fw__thumb--active" : ""}`}
                  onClick={() => { goTo(origIdx, origIdx > active ? 1 : -1); startTimeRef.current = Date.now(); }}
                >
                  <div
                    className="fw__thumb-img"
                    style={loadedImages.has(origIdx) ? { backgroundImage: `url(${proj.thumb})` } : undefined}
                  />
                  <div className="fw__thumb-info">
                    <span className="fw__thumb-num" style={isActive ? { color: proj.accent } : {}}>
                      {proj.number}
                    </span>
                  </div>
                  <div
                    className="fw__thumb-bar"
                    style={isActive ? {
                      transform: `scaleX(${progress / 100})`,
                      WebkitTransform: `scaleX(${progress / 100})`,
                      background: proj.accent,
                      opacity: 0.6,
                    } : {}}
                  />
                </button>
              );
            })}
          </div>

          {/* Nav arrows */}
          <div className="fw__sidebar-nav">
            <button
              className="fw__nav-btn"
              onClick={() => { goTo((active - 1 + PROJECTS.length) % PROJECTS.length, -1); startTimeRef.current = Date.now(); }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              className="fw__nav-btn"
              onClick={() => { advance(); startTimeRef.current = Date.now(); }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M5 2l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

      </div>
    </section>
  );
}

// import React, { useEffect, useRef, useState, useCallback } from "react";
// import "./Home.scss";

// // ── Replace images/titles/descriptions with your actual project data ──────


// const AUTO_INTERVAL = 3000; // ms between auto-advances

// export default function FeaturedWorkSection({ registerIntent,url }) {

// const PROJECTS = [
//   {
//     id: 1,
//     number: "01",
//     image: url + "images/new-home/featured-work/1.jpg",
//     thumb: url + "images/new-home/featured-work/1.jpg",
//   },
//   {
//     id: 2,
//     number: "02",
//     image: url + "images/new-home/featured-work/2.jpg",
//     thumb: url + "images/new-home/featured-work/2.jpg",
//   },
//   {
//     id: 3,
//     number: "03",
//     image: url + "images/new-home/featured-work/3.jpg",
//     thumb: url + "images/new-home/featured-work/3.jpg",
//   },
//   {
//     id: 4,
//     number: "04",
//     image: url + "images/new-home/featured-work/4.jpg",
//     thumb: url + "images/new-home/featured-work/4.jpg",
//   },
//   {
//     id: 5,
//     number: "05",
//     image: url + "images/new-home/featured-work/5.jpg",
//     thumb: url + "images/new-home/featured-work/5.jpg",
//   },
//   {
//     id: 6,
//     number: "06",
//     image: url + "images/new-home/featured-work/6.jpg",
//     thumb: url + "images/new-home/featured-work/6.jpg",
//   },
//   {
//     id: 7,
//     number: "07",
//     image: url + "images/new-home/featured-work/7.jpg",
//     thumb: url + "images/new-home/featured-work/7.jpg",
//   },
//   {
//     id: 8,
//     number: "08",
//     image: url + "images/new-home/featured-work/8.jpg",
//     thumb: url + "images/new-home/featured-work/8.jpg",
//   },
//   {
//     id: 9,
//     number: "09",
//     image: url + "images/new-home/featured-work/9.jpg",
//     thumb: url + "images/new-home/featured-work/9.jpg",
//   },
//   {
//     id: 10,
//     number: "10",
//     image: url + "images/new-home/featured-work/10.jpg",
//     thumb: url + "images/new-home/featured-work/10.jpg",
//   },
//   {
//     id: 11,
//     number: "11",
//     image: url + "images/new-home/featured-work/11.jpg",
//     thumb: url + "images/new-home/featured-work/11.jpg",
//   },
// ];


//   const [active, setActive]   = useState(0);
//   const [prev,   setPrev]     = useState(null);
//   const [dir,    setDir]      = useState(1);
//   const [paused, setPaused]   = useState(false);
//   const [progress, setProgress] = useState(0);
 
//   const activeRef     = useRef(0);
//   const timerRef      = useRef(null);
//   const progressRef   = useRef(null);
//   const startTimeRef  = useRef(Date.now());
//   const transitioning = useRef(false);
 
//   const goTo = useCallback((idx, direction = 1) => {
//     if (idx === activeRef.current) return;
//     setPrev(activeRef.current);
//     setDir(direction);
//     activeRef.current = idx;
//     setActive(idx);
//     setProgress(0);
//     startTimeRef.current = Date.now();
//   }, []);
 
//   const advance = useCallback(() => {
//     const next = (activeRef.current + 1) % PROJECTS.length;
//     goTo(next, 1);
//   }, [goTo]);
 
//   // Auto-advance timer
//   useEffect(() => {
//     if (paused) return;
 
//     const tick = () => {
//       const elapsed = Date.now() - startTimeRef.current;
//       const pct = Math.min((elapsed / AUTO_INTERVAL) * 100, 100);
//       setProgress(pct);
//       if (elapsed >= AUTO_INTERVAL) {
//         advance();
//         startTimeRef.current = Date.now();
//       }
//       progressRef.current = requestAnimationFrame(tick);
//     };
 
//     progressRef.current = requestAnimationFrame(tick);
//     return () => cancelAnimationFrame(progressRef.current);
//   }, [paused, advance]);
 
//   // Register scroll intent for ScrollyLayout
//   useEffect(() => {
//     if (!registerIntent) return;
//     registerIntent((scrollDir) => {
//       const next = activeRef.current + scrollDir;
//       if (next >= 0 && next < PROJECTS.length) {
//         goTo(next, scrollDir);
//         startTimeRef.current = Date.now();
//         return true;
//       }
//       return false;
//     });
//   }, [registerIntent, goTo]);
 
//   const p = PROJECTS[active];
 
//   return (
//     <section
//       id="work-section"
//       className="fw"
//       onMouseEnter={() => setPaused(true)}
//       onMouseLeave={() => { setPaused(false); startTimeRef.current = Date.now(); }}
//     >
//       {/* ── Background image (blurred) ── */}
//       {PROJECTS.map((proj, i) => (
//         <div
//           key={proj.id}
//           className={`fw__bg ${i === active ? "fw__bg--active" : ""}`}
//           style={{ backgroundImage: `url(${proj.image})` }}
//         />
//       ))}
//       <div className="fw__bg-overlay" />
 
//       {/* ── Top label ── */}
//       {/* <div className="fw__header">
//         <div className="fw__label">
//           <span className="fw__label-line" />
//           Featured Work
//         </div>
//         <div className="fw__header-count">
//           <span style={{ color: p.accent }}>{String(active + 1).padStart(2,"0")}</span>
//           <span className="fw__header-sep" />
//           {String(PROJECTS.length).padStart(2,"0")}
//         </div>
//       </div> */}
 
//       {/* ── Main layout ── */}
//       <div className="fw__body">
 
//         {/* CENTER: Main showcase screen */}
//         <div className="fw__screen-wrap">
//           <div className="fw__screen">
//             {/* Screen bezel */}
//             <div className="fw__screen-bezel">
//               <div className="fw__screen-cam" />
//             </div>
//             {/* Screen images */}
//             <div className="fw__screen-display">
//               {PROJECTS.map((proj, i) => (
//                 <div
//                   key={proj.id}
//                   className={`fw__screen-img ${
//                     i === active ? "fw__screen-img--active" :
//                     i === prev   ? `fw__screen-img--exit fw__screen-img--exit-${dir > 0 ? "left" : "right"}` : ""
//                   }`}
//                   style={{ backgroundImage: `url(${proj.image})` }}
//                 />
//               ))}
//               {/* Subtle screen glare */}
//               <div className="fw__screen-glare" />
//             </div>
//             {/* Progress bar at bottom of screen */}
//             <div className="fw__screen-progress">
//               <div
//                 className="fw__screen-progress-fill"
//                 style={{ width: `${progress}%`, background: p.accent }}
//               />
//             </div>
//             {/* Screen stand */}
//           </div>
//           <div className="fw__stand">
//             <div className="fw__stand-neck" />
//             <div className="fw__stand-base" />
//           </div>
//         </div>
 
//         {/* RIGHT: Thumbnail sidebar */}
//         <div className="fw__sidebar">
//           <p className="fw__sidebar-label">Projects</p>
//           <div className="fw__thumbs">
//             {PROJECTS.map((proj, i) => (
//               <button
//                 key={proj.id}
//                 className={`fw__thumb ${i === active ? "fw__thumb--active" : ""}`}
//                 onClick={() => { goTo(i, i > active ? 1 : -1); startTimeRef.current = Date.now(); }}
//                 style={i === active ? { "--accent": proj.accent } : {}}
//               >
//                 {/* Thumbnail image */}
//                 <div
//                   className="fw__thumb-img"
//                   style={{ backgroundImage: `url(${proj.thumb})` }}
//                 />
//                 {/* Info */}
//                 <div className="fw__thumb-info">
//                   <span className="fw__thumb-num" style={i === active ? { color: proj.accent } : {}}>
//                     {proj.number}
//                   </span>
//                   {/* <span className="fw__thumb-title">{proj.title}</span>
//                   <span className="fw__thumb-cat">{proj.category}</span> */}
//                 </div>
//                 {/* Progress fill bar — grows as timer counts down */}
//                 <div
//                   className="fw__thumb-bar"
//                   style={i === active ? {
//                     transform: `scaleX(${progress / 100})`,
//                     WebkitTransform: `scaleX(${progress / 100})`,
//                     background: proj.accent,
//                     opacity: 0.6,
//                   } : {}}
//                 />
//               </button>
//             ))}
//           </div>
 
//           {/* Sidebar navigation arrows */}
//           <div className="fw__sidebar-nav">
//             <button
//               className="fw__nav-btn"
//               onClick={() => { goTo((active - 1 + PROJECTS.length) % PROJECTS.length, -1); startTimeRef.current = Date.now(); }}
//             >
//               <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
//                 <path d="M9 2L4 7l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
//               </svg>
//             </button>
//             <button
//               className="fw__nav-btn"
//               onClick={() => { advance(); startTimeRef.current = Date.now(); }}
//             >
//               <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
//                 <path d="M5 2l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
//               </svg>
//             </button>
//           </div>
//         </div>
 
//       </div>
 
//       {/* ── Bottom progress dots ── */}
//       {/* <div className="fw__dots">
//         {PROJECTS.map((proj, i) => (
//           <button
//             key={proj.id}
//             className={`fw__dot ${i === active ? "fw__dot--active" : ""}`}
//             onClick={() => { goTo(i, i > active ? 1 : -1); startTimeRef.current = Date.now(); }}
//             style={i === active ? { background: proj.accent, boxShadow: `0 0 8px ${proj.accent}` } : {}}
//           />
//         ))}
//       </div> */}
 
//     </section>
//   );
// }